import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Phone, Video, Save, Plus, Trash2, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONSULTATION_TYPES = [
  { value: 'TEXT', label: 'Text', icon: MessageSquare, description: 'Text-based messaging consultation' },
  { value: 'AUDIO', label: 'Audio', icon: Phone, description: 'Phone or audio call' },
  { value: 'VIDEO', label: 'Video', icon: Video, description: 'Video call (Zoom, Meet, FaceTime, etc.)' }
];

const AVAILABILITY_MODES = [
  { value: 'OPEN_DOOR', label: 'Open Door Only', description: 'Clients can reach you during open hours' },
  { value: 'SCHEDULED', label: 'Scheduled Only', description: 'Clients book specific time slots' },
  { value: 'BOTH', label: 'Both', description: 'Offer both open door and scheduled consultations' }
];

const OPEN_DOOR_MODES = [
  { value: 'ONE_ON_ONE', label: 'One-on-One', description: 'One client at a time' },
  { value: 'MULTIPLE_CONVERSATIONS', label: 'Multiple Conversations', description: 'Handle multiple clients simultaneously' }
];

const PRICING_TYPES = [
  { value: 'FREE', label: 'Free' },
  { value: 'FIXED', label: 'Fixed Price' },
  { value: 'HOURLY', label: 'Hourly Rate' }
];

const PAYMENT_TIMINGS = [
  { value: 'FREE', label: 'Free (No Payment)' },
  { value: 'BEFORE', label: 'Payment Before' },
  { value: 'AFTER', label: 'Payment After' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

export default function ConsultationSettingsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    offersConsultations: false,
    consultationTypes: [],
    availabilityMode: 'SCHEDULED',
    openDoorMode: 'ONE_ON_ONE',
    maxOpenDoorClients: 1,
    consultationInstructions: '',
    consultationPricing: []
  });
  
  // Open door blocks
  const [openDoorBlocks, setOpenDoorBlocks] = useState([]);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [newBlock, setNewBlock] = useState({
    dayOfWeek: 0,
    startTime: '09:00',
    endTime: '17:00',
    consultationTypes: [],
    openDoorMode: 'ONE_ON_ONE',
    maxClients: 1
  });

  useEffect(() => {
    loadSettings();
    loadOpenDoorBlocks();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/consultation-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        offersConsultations: response.data.offersConsultations || false,
        consultationTypes: response.data.consultationTypes || [],
        availabilityMode: response.data.availabilityMode || 'SCHEDULED',
        openDoorMode: response.data.openDoorMode || 'ONE_ON_ONE',
        maxOpenDoorClients: response.data.maxOpenDoorClients || 1,
        consultationInstructions: response.data.consultationInstructions || '',
        consultationPricing: response.data.consultationPricing || []
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load consultation settings');
    } finally {
      setLoading(false);
    }
  };

  const loadOpenDoorBlocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/open-door-blocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDoorBlocks(response.data || []);
    } catch (error) {
      console.error('Error loading open door blocks:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/dashboard/consultation-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consultation settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleConsultationType = (type) => {
    const types = [...settings.consultationTypes];
    const index = types.indexOf(type);
    if (index > -1) {
      types.splice(index, 1);
      // Remove pricing for this type
      const pricing = settings.consultationPricing.filter(p => p.consultationType !== type);
      setSettings({ ...settings, consultationTypes: types, consultationPricing: pricing });
    } else {
      types.push(type);
      // Add default pricing for this type
      const pricing = [...settings.consultationPricing, {
        consultationType: type,
        pricingType: 'FREE',
        price: null,
        paymentTiming: 'FREE'
      }];
      setSettings({ ...settings, consultationTypes: types, consultationPricing: pricing });
    }
  };

  const updatePricing = (type, field, value) => {
    const pricing = settings.consultationPricing.map(p => {
      if (p.consultationType === type) {
        return { ...p, [field]: value };
      }
      return p;
    });
    setSettings({ ...settings, consultationPricing: pricing });
  };

  const addOpenDoorBlock = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/dashboard/open-door-blocks`, newBlock, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDoorBlocks([...openDoorBlocks, response.data.block]);
      setShowAddBlock(false);
      setNewBlock({
        dayOfWeek: 0,
        startTime: '09:00',
        endTime: '17:00',
        consultationTypes: [],
        openDoorMode: 'ONE_ON_ONE',
        maxClients: 1
      });
      toast.success('Open door block added');
    } catch (error) {
      console.error('Error adding block:', error);
      toast.error('Failed to add block');
    }
  };

  const deleteOpenDoorBlock = async (blockId) => {
    try {
      await axios.delete(`${API_URL}/api/dashboard/open-door-blocks/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenDoorBlocks(openDoorBlocks.filter(b => b.id !== blockId));
      toast.success('Block deleted');
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error('Failed to delete block');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Consultation Settings</h1>
            <p className="text-muted-foreground">Configure how clients can book consultations with you</p>
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Settings
          </Button>
        </div>

        {/* Enable Consultations */}
        <Card>
          <CardHeader>
            <CardTitle>Enable Consultations</CardTitle>
            <CardDescription>Allow clients to book consultations with you</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <Switch
                checked={settings.offersConsultations}
                onCheckedChange={(checked) => setSettings({ ...settings, offersConsultations: checked })}
              />
              <Label>Offer Consultations</Label>
            </div>
          </CardContent>
        </Card>

        {settings.offersConsultations && (
          <>
            {/* Consultation Types */}
            <Card>
              <CardHeader>
                <CardTitle>Consultation Types</CardTitle>
                <CardDescription>Select the types of consultations you offer</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {CONSULTATION_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = settings.consultationTypes.includes(type.value);
                  return (
                    <div key={type.value} className="flex items-start space-x-4 p-4 border rounded-lg">
                      <Switch
                        checked={isSelected}
                        onCheckedChange={() => toggleConsultationType(type.value)}
                      />
                      <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <Label className="font-medium">{type.label}</Label>
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Pricing */}
            {settings.consultationTypes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Consultation Pricing</CardTitle>
                  <CardDescription>Set pricing for each consultation type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {settings.consultationTypes.map((type) => {
                    const typeInfo = CONSULTATION_TYPES.find(t => t.value === type);
                    const pricing = settings.consultationPricing.find(p => p.consultationType === type) || {};
                    const Icon = typeInfo?.icon || MessageSquare;
                    return (
                      <div key={type} className="p-4 border rounded-lg space-y-4">
                        <div className="flex items-center space-x-2">
                          <Icon className="h-5 w-5" />
                          <Label className="font-medium">{typeInfo?.label}</Label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label>Pricing Type</Label>
                            <Select
                              value={pricing.pricingType || 'FREE'}
                              onValueChange={(value) => updatePricing(type, 'pricingType', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PRICING_TYPES.map((pt) => (
                                  <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {pricing.pricingType !== 'FREE' && (
                            <div>
                              <Label>Price ($)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={(pricing.price || 0) / 100}
                                onChange={(e) => updatePricing(type, 'price', Math.round(parseFloat(e.target.value) * 100))}
                                placeholder="0.00"
                              />
                            </div>
                          )}
                          <div>
                            <Label>Payment Timing</Label>
                            <Select
                              value={pricing.paymentTiming || 'FREE'}
                              onValueChange={(value) => updatePricing(type, 'paymentTiming', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAYMENT_TIMINGS.map((pt) => (
                                  <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Availability Mode */}
            <Card>
              <CardHeader>
                <CardTitle>Availability Mode</CardTitle>
                <CardDescription>How do you want to offer consultations?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {AVAILABILITY_MODES.map((mode) => (
                  <div
                    key={mode.value}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      settings.availabilityMode === mode.value ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setSettings({ ...settings, availabilityMode: mode.value })}
                  >
                    <Label className="font-medium cursor-pointer">{mode.label}</Label>
                    <p className="text-sm text-muted-foreground">{mode.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Open Door Settings */}
            {(settings.availabilityMode === 'OPEN_DOOR' || settings.availabilityMode === 'BOTH') && (
              <Card>
                <CardHeader>
                  <CardTitle>Open Door Settings</CardTitle>
                  <CardDescription>Configure your open door availability</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label>Open Door Mode</Label>
                    {OPEN_DOOR_MODES.map((mode) => (
                      <div
                        key={mode.value}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          settings.openDoorMode === mode.value ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'
                        }`}
                        onClick={() => setSettings({ ...settings, openDoorMode: mode.value })}
                      >
                        <Label className="font-medium cursor-pointer">{mode.label}</Label>
                        <p className="text-sm text-muted-foreground">{mode.description}</p>
                      </div>
                    ))}
                  </div>

                  {settings.openDoorMode === 'MULTIPLE_CONVERSATIONS' && (
                    <div>
                      <Label>Max Clients at Once</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={settings.maxOpenDoorClients}
                        onChange={(e) => setSettings({ ...settings, maxOpenDoorClients: parseInt(e.target.value) || 1 })}
                        className="w-32"
                      />
                    </div>
                  )}

                  {/* Open Door Blocks */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-lg">Open Door Hours</Label>
                      <Button variant="outline" size="sm" onClick={() => setShowAddBlock(true)}>
                        <Plus className="h-4 w-4 mr-2" /> Add Block
                      </Button>
                    </div>

                    {openDoorBlocks.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No open door blocks configured</p>
                    ) : (
                      <div className="space-y-2">
                        {openDoorBlocks.map((block) => (
                          <div key={block.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center space-x-4">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{DAYS_OF_WEEK.find(d => d.value === block.dayOfWeek)?.label}</span>
                              <span>{block.startTime} - {block.endTime}</span>
                              <span className="text-sm text-muted-foreground">
                                {block.consultationTypes?.join(', ')}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteOpenDoorBlock(block.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {showAddBlock && (
                      <div className="p-4 border rounded-lg space-y-4 bg-muted/50">
                        <Label className="font-medium">Add Open Door Block</Label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label>Day</Label>
                            <Select
                              value={newBlock.dayOfWeek.toString()}
                              onValueChange={(value) => setNewBlock({ ...newBlock, dayOfWeek: parseInt(value) })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DAYS_OF_WEEK.map((day) => (
                                  <SelectItem key={day.value} value={day.value.toString()}>{day.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Start Time</Label>
                            <Input
                              type="time"
                              value={newBlock.startTime}
                              onChange={(e) => setNewBlock({ ...newBlock, startTime: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>End Time</Label>
                            <Input
                              type="time"
                              value={newBlock.endTime}
                              onChange={(e) => setNewBlock({ ...newBlock, endTime: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Consultation Types</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {CONSULTATION_TYPES.map((type) => (
                              <Button
                                key={type.value}
                                variant={newBlock.consultationTypes.includes(type.value) ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  const types = [...newBlock.consultationTypes];
                                  const index = types.indexOf(type.value);
                                  if (index > -1) {
                                    types.splice(index, 1);
                                  } else {
                                    types.push(type.value);
                                  }
                                  setNewBlock({ ...newBlock, consultationTypes: types });
                                }}
                              >
                                {type.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button onClick={addOpenDoorBlock} disabled={newBlock.consultationTypes.length === 0}>
                            Add Block
                          </Button>
                          <Button variant="outline" onClick={() => setShowAddBlock(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Meeting Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Meeting Instructions</CardTitle>
                <CardDescription>Instructions for clients (phone number, Zoom link, etc.)</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={settings.consultationInstructions || ''}
                  onChange={(e) => setSettings({ ...settings, consultationInstructions: e.target.value })}
                  placeholder="Enter instructions for clients, such as:
- Call me at (555) 123-4567
- Join via Zoom: https://zoom.us/j/...
- FaceTime: your@email.com"
                  rows={5}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
